<template>
    <div class="pa-4">
        <v-skeleton-loader v-if="initialLoading" type="card, table"></v-skeleton-loader>
        <div v-if="!initialLoading && data">
            <v-overlay :model-value="reloading" class="align-center justify-center">
                <v-progress-circular color="primary" indeterminate size="64"></v-progress-circular>
            </v-overlay>
            <!-- Date selector -->
            <v-row class="mb-4" align="center">
                <v-col cols="6">
                    <v-text-field
                        v-model="asAt"
                        label="As At Date"
                        type="date"
                        variant="outlined"
                        density="compact"
                        hide-details
                        :max="todayStr"
                        @change="onAsAtChange"
                    ></v-text-field>
                </v-col>
                <v-col cols="6" class="text-right">
                    <stocktake-loader></stocktake-loader>
                </v-col>
            </v-row>

            <!-- Summary cards -->
            <v-row class="mb-4">
                <v-col
                    v-for="group in data.colourGroups"
                    :key="group.name"
                    cols="12"
                    sm="6"
                    md="4"
                    lg="2"
                >
                    <v-card color="indigo-lighten-1" variant="flat" height="100%" class="text-center">
                        <v-card-text class="pt-1">
                            <h2 class="mb-1 pb-1 border-b-sm"><v-icon start icon="mdi-palette-swatch-outline"></v-icon> {{ group.name }}</h2>
                            <div>
                                <div class="mb-2">
                                    <div class="header">QUANTITY</div>
                                    <h2>{{ numberWithCommas(group.quantity, 2) }} kg</h2>
                                </div>
                                <div>
                                    <div class="header">VALUE</div>
                                    <h2>${{ numberWithCommas(group.value, 2) }}</h2>
                                </div>
                            </div>
                        </v-card-text>
                    </v-card>
                </v-col>
                <!-- Total card -->
                <v-col cols="12" sm="6" md="4" lg="2">
                    <v-card color="blue-grey-darken-1" variant="flat" height="100%" class="text-center">
                        <v-card-text class="pt-1">
                            <h2 class="mb-1 pb-1 border-b-sm"><v-icon start icon="mdi-equal"></v-icon> Total</h2>
                            <div>
                                <div class="mb-2">
                                    <div class="header" style="opacity:0.8">QUANTITY</div>
                                    <h2>{{ numberWithCommas(data.totalQuantity, 2) }} kg</h2>
                                </div>
                                <div>
                                    <div class="header" style="opacity:0.8">VALUE</div>
                                    <h2>${{ numberWithCommas(data.totalValue, 2) }}</h2>
                                </div>
                            </div>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <!-- Search + filter controls -->
            <v-row class="mb-2" align="center">
                <v-col cols="12" sm="6" md="4">
                    <v-text-field
                        v-model="search"
                        prepend-inner-icon="mdi-magnify"
                        label="Search colours"
                        variant="outlined"
                        density="compact"
                        hide-details
                        clearable
                    ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6" md="8">
                    <div class="d-flex flex-wrap ga-2">
                        <v-chip
                            v-for="group in data.colourGroups"
                            :key="group.name"
                            :color="selectedGroup === group.name ? 'primary' : undefined"
                            :variant="selectedGroup === group.name ? 'flat' : 'outlined'"
                            class="cursor-pointer"
                            @click="toggleGroupFilter(group.name)"
                        >
                            {{ group.name }}
                        </v-chip>
                    </div>
                </v-col>
            </v-row>

            <!-- Colours table -->
            <v-data-table
                :headers="headers"
                :items="filteredColours"
                density="compact"
                :sort-by="[{ key: 'value', order: 'desc' }]"
            >
                <template #item.name="{ item }">
                    <span v-html="formatSearchName(item.name, search)"></span>
                </template>
                <template #item.quantity="{ item }">
                    {{ numberWithCommas(item.quantity, 2) }} kg
                </template>
                <template #item.buy="{ item }">
                    <span v-if="item.buy != null">${{ numberWithCommas(item.buy, 2) }}</span>
                    <span v-else class="text-disabled">—</span>
                </template>
                <template #item.value="{ item }">
                    ${{ numberWithCommas(itemValue(item), 2) }}
                </template>
            </v-data-table>
        </div>
    </div>
</template>
<style scoped>
</style>
<script>
    import { mapActions } from "vuex";
    import { numberWithCommas } from "../mixins/numberMixins";
    import { formatSearchName } from "../mixins/searchMixins";
    function todayString() {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }
    function initialState() {
        return {
            saving: false,
            initialLoading: false,
            reloading: false,
            data: null,
            search: '',
            selectedGroup: null,
            asAt: todayString(),
        };
    }
    export default {
        name: "StockView",
        data: function () {
            return initialState();
        },
        mounted: function () {
            this.loadData(true);
        },
        props: {
        },
        methods: {
            ...mapActions([
                "showErrorSnack",
                "showSuccessSnack",
            ]),
            formatSearchName,
            numberWithCommas,
            reset: function () {
                Object.assign(this.$data, initialState());
            },
            onAsAtChange() {
                this.loadData(false);
            },
            loadData(initial) {
                if (initial) {
                    this.initialLoading = true;
                } else {
                    this.reloading = true;
                }
                this.$api.get('dashboard/stocktotal', { params: { asAt: this.asAt } })
                    .then(res => {
                        this.data = res.data;
                    })
                    .catch(() => {
                        this.showErrorSnack('There was an issue loading stock data');
                    })
                    .finally(() => {
                        this.initialLoading = false;
                        this.reloading = false;
                    });
            },
            toggleGroupFilter(name) {
                this.selectedGroup = this.selectedGroup === name ? null : name;
            },
            itemValue(item) {
                const group = this.data?.colourGroups.find(g => g.name === item.colourGroup);
                const buy = item.buy ?? group?.averageBuy ?? 0;
                return buy * item.quantity;
            },
        },
        computed: {
            todayStr() {
                return todayString();
            },
            headers() {
                return [
                    { title: 'Name', key: 'name' },
                    { title: 'Group', key: 'colourGroup' },
                    { title: 'Buy Price', key: 'buy' },
                    { title: 'Quantity', key: 'quantity' },
                    { title: 'Value', key: 'value', sortable: false },
                ];
            },
            allColours() {
                if (!this.data) return [];
                return this.data.colourGroups.flatMap(g => g.colours);
            },
            filteredColours() {
                let colours = this.allColours;
                if (this.selectedGroup) {
                    colours = colours.filter(c => c.colourGroup === this.selectedGroup);
                }
                if (this.search && this.search.trim()) {
                    const q = this.search.trim().toLowerCase();
                    colours = colours.filter(c => c.name?.toLowerCase().includes(q));
                }
                return colours.slice().sort((a, b) => this.itemValue(b) - this.itemValue(a));
            },
        }
    };
</script>
